"use client";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { PublicationModel } from "../../types/models";
import Button from "../Button";
import CustomTooltip from "../CustomTooltip";

export default function CalendarPostNav({
  previous,
  next,
  index,
  count,
  onNavigate,
}: {
  previous: PublicationModel | null;
  next: PublicationModel | null;
  index: number;
  count: number;
  onNavigate: (publication: PublicationModel) => void;
}) {
  const tooltipId = useId();
  const groupRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const hasNavigation = index >= 0 && count > 1;
  const visible = hasNavigation && isOpen;
  const positionLabel = `Post ${index + 1} of ${count}`;

  const cancelHover = () => {
    if (hoverTimer.current !== null) clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  };
  const show = () => {
    cancelHover();
    setIsOpen(true);
  };
  const hide = () => {
    cancelHover();
    setIsOpen(false);
  };

  useEffect(() => () => {
    if (hoverTimer.current !== null) clearTimeout(hoverTimer.current);
  }, []);

  useEffect(() => {
    if (!visible) return;
    // Dismiss this layer first. A second Escape can close the inspector.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !groupRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [visible]);

  if (!hasNavigation) return null;

  return (
    <>
      <div
        data-calendar-post-navigation
        ref={groupRef}
        role="group"
        className="pointer-events-auto flex items-center gap-0"
        aria-label="Browse posts for this short"
        data-tooltip-id={tooltipId}
        onPointerEnter={(event) => {
          if (event.pointerType === "touch") return;
          cancelHover();
          hoverTimer.current = setTimeout(() => setIsOpen(true), 200);
        }}
        onPointerLeave={hide}
        onFocusCapture={show}
        onBlurCapture={(event) => {
          if (event.currentTarget.contains(event.relatedTarget)) return;
          hide();
        }}
      >
        {([
          { post: previous, direction: "Previous", Icon: ChevronLeftIcon },
          { post: next, direction: "Next", Icon: ChevronRightIcon },
        ] as const).map(({ post, direction, Icon }) => (
          <Button
            key={direction}
            type="button"
            size="s"
            variant="ghost"
            className="shrink-0 aria-disabled:cursor-default aria-disabled:opacity-50 aria-disabled:hover:bg-transparent"
            // Keep focus when a step reaches an endpoint; the click guard
            // makes the unavailable direction inert for mouse and keyboard.
            aria-disabled={!post}
            aria-label={`${direction} post for this short`}
            aria-describedby={visible ? tooltipId : undefined}
            onClick={() => {
              if (!post) return;
              show();
              onNavigate(post);
            }}
          >
            <Icon className="w-4 h-4" />
          </Button>
        ))}
      </div>
      {visible && createPortal(
        <CustomTooltip
          id={tooltipId}
          imperativeModeOnly
          isOpen
          className="whitespace-nowrap"
        >
          <span aria-live="polite" aria-atomic="true">
            <span className="text-tooltip-foreground">{positionLabel}</span>{" "}
            <span className="text-slate-400">for this short</span>
          </span>
        </CustomTooltip>,
        document.body,
      )}
    </>
  );
}
